/**
 * THE COMMAND LINE — one docked console, both platforms (cockpit bundle,
 * reference DC). Replaces the floating trigger + modal omnibar: the input is
 * ALWAYS present at the bottom of the shell; desktop is keyboard-first
 * (type-anywhere focuses it), mobile is vocal-first (hot-mic button).
 *
 * Trust model unchanged (behavior contract §3): the brain only ever PROPOSES —
 * apply-cards are the sole mutation path, ↵ = tout appliquer, 1–9 = one card,
 * esc = dismiss, ⌘Z = undo through the real inverse-op path. The heavy brain
 * (omnibar/brain.ts + vault index) is dynamic-imported on first submit so the
 * shell stays light (weight law).
 *
 * Honest seams: slash commands whose backends land in P4/P5 (pdf, mail,
 * drive, ics, sacem, missions) answer with WHEN they arrive — never a fake
 * success (CONSTRAINTS: failures render as failures).
 */
import { useEffect, useRef, useState } from "react";
import { t, tLabel, type Locale, type MessageKey } from "../i18n.ts";
import type { GigRecord } from "../db/idb.ts";
import type { Editor } from "../outbox/mutations.ts";
import type { UploadQueue } from "../files/uploadQueue.ts";
import type { ApplyCard, OmnibarResult } from "../omnibar/brain.ts";
import type { SplitId } from "../views/splits.ts";

export type CockpitView = "gig" | "vault" | "rolo" | "settings";

/** Gig-header field key → its GIG SHEET label key, for the "inferred" evidence
 * line (Design Code §8). Only the keys whose label differs from the default
 * `gigfiche.<field>` need listing; the rest fall through to that fallback. */
const INFERRED_LABEL: Record<string, string> = {
  doors_time: "gigfiche.doors",
  soundcheck_time: "gigfiche.soundcheck",
  ticket_url: "gigfiche.tickets",
  venue_address: "gigfiche.address",
};

/** Impact-before-action (Design Code §12): a calm one-line summary of what
 * apply will create, flagging personal data (the PII flag is decided server-side
 * from the registry's field-level pii markers — the client stays light). */
function planImpact(
  locale: Locale,
  plan: { gigs: unknown[]; rows: { collection: string }[]; personalData: boolean },
): string {
  const parts: string[] = [];
  if (plan.gigs.length > 0) parts.push(t(locale, "console.plan_dates", { n: plan.gigs.length }));
  const byColl = new Map<string, number>();
  for (const r of plan.rows) byColl.set(r.collection, (byColl.get(r.collection) ?? 0) + 1);
  for (const [coll, n] of byColl) parts.push(`${n} ${tLabel(locale, `collections.${coll}`)}`);
  const base = `${t(locale, "console.plan_creates")} ${parts.join(" · ")}`;
  return plan.personalData ? `${base} · ${t(locale, "console.plan_pii")}` : base;
}

export interface ConsoleAnswer {
  text: string;
  kind: "ok" | "warn" | "work";
}

interface LogLine {
  mark: string;
  tone: "cmd" | "ai";
  text: string;
}

export interface TutorialState {
  step: number; // -1 = off
  total: number;
}

export interface ConsoleProps {
  locale: Locale;
  gigs: readonly GigRecord[];
  focusGig: GigRecord | null;
  editor: Editor | null;
  canWrite: boolean;
  isMobile: boolean;
  /** Where the user is — the console speaks that surface's language. */
  view: CockpitView;
  uploadQueue?: UploadQueue;
  onJump(gigId: string, split: SplitId): void;
  onView(view: CockpitView): void;
  onOpenPerson(nameQuery: string): void;
  onStartTutorial(): void;
  onNewGig(prefill?: { city?: string; day?: string }): void;
  onAddRow(): void;
  onForceSave(): void;
  onOpenFile(fileItemId: string): void;
  pushJournal(text: string, tone: "ok" | "warn" | "muted" | "accent"): void;
  tutorialTick(evt: "cards" | "apply" | "undo" | "view"): void;
  tutorial: TutorialState;
  onTutorialNext(): void;
  onTutorialQuit(): void;
  setPrefs(patch: { theme?: "auto" | "night" | "day"; lang?: "fr" | "en"; units?: "km" | "mi" }): void;
  /** Rail alerts pre-fill the console; App hands us a registration hook. */
  registerPrefill?(fn: (q: string) => void): void;
  /** Fleet: dispatch an agent mission (audit/advance) on a gig. */
  onDispatchMission?(mission: "audit" | "advance", gigId: string): void;
  /** Rail «[n cartes]» loads a finished mission's validated cards here. */
  registerExternalCards?(fn: (cards: readonly ApplyCard[], context: string) => void): void;
}

type BrainModule = typeof import("../omnibar/brain.ts");

interface SlashCmd {
  cmd: string;
  descKey: MessageKey;
  run(): void;
}

export function Console(props: ConsoleProps) {
  const { locale, editor, canWrite, isMobile } = props;
  const [query, setQuery] = useState("");
  const [cards, setCards] = useState<readonly ApplyCard[]>([]);
  const [cardsContext, setCardsContext] = useState("");
  const [answer, setAnswer] = useState<ConsoleAnswer | null>(null);
  const [log, setLog] = useState<readonly LogLine[]>([]);
  const [listening, setListening] = useState(false);
  /** A validated bulk-creation plan from the megaprompt brain. */
  const [plan, setPlan] = useState<{
    gigs: Record<string, string>[];
    rows: { gig_ref: string; collection: string; fields: Record<string, string> }[];
    note: string;
    inferred: { field: string; value: string }[];
    personalData: boolean;
  } | null>(null);
  /** LIVE SEARCH (the DC behavior): results render as you type — ranked by
   * the brain's time+context scoring (today owns the console, the week
   * beats later, the past sinks, focus-gig floats its files). */
  const [hits, setHits] = useState<readonly import("../omnibar/brain.ts").JumpHit[]>([]);
  const [fileHits, setFileHits] = useState<readonly import("../files/vault.ts").VaultFile[]>([]);
  const [sel, setSel] = useState(0);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeq = useRef(0);
  /** Empty console ≠ empty (DC law): focus with no query shows what's NEXT
   * + your recent commands, re-runnable in one tap. */
  const [focused, setFocused] = useState(false);
  // The menu evaporates rather than vanishing (Eduardo 2026-07-03): on blur it
  // lingers ~300ms with the `.evaporating` class (blur + rise + fade), then
  // unmounts. Also fixes the "stuck menu" — every menu block now gates on this,
  // so clicking away always dismisses (the results block used to ignore focus).
  const [closing, setClosing] = useState(false);
  useEffect(() => {
    if (focused) { setClosing(false); return; }
    setClosing(true);
    const id = setTimeout(() => setClosing(false), 300);
    return () => clearTimeout(id);
  }, [focused]);
  const menuOpen = focused || closing;
  const evaporating = focused ? "" : " evaporating";
  const [recents, setRecents] = useState<readonly string[]>(() => {
    try { return JSON.parse(localStorage.getItem("td-recent") ?? "[]") as string[]; } catch { return []; }
  });
  const remember = (q: string) => {
    setRecents((r) => {
      const next = [q, ...r.filter((x) => x !== q)].slice(0, 4);
      try { localStorage.setItem("td-recent", JSON.stringify(next)); } catch { /* private */ }
      return next;
    });
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const brainRef = useRef<BrainModule | null>(null);
  const answerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const say = (text: string, kind: ConsoleAnswer["kind"] = "ok", ephemeralMs = 0) => {
    setAnswer({ text, kind });
    if (answerTimer.current !== null) clearTimeout(answerTimer.current);
    if (ephemeralMs > 0) answerTimer.current = setTimeout(() => setAnswer(null), ephemeralMs);
  };
  const pushLog = (mark: string, tone: LogLine["tone"], text: string) =>
    setLog((l) => [...l, { mark, tone, text }].slice(-5));

  useEffect(() => {
    props.registerPrefill?.((q) => {
      setQuery(q);
      inputRef.current?.focus();
    });
    props.registerExternalCards?.((externalCards, context) => {
      setCards(externalCards);
      setCardsContext(context);
      setQuery("");
      setAnswer(null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const brain = async (): Promise<BrainModule> => {
    if (brainRef.current === null) brainRef.current = await import("../omnibar/brain.ts");
    return brainRef.current;
  };

  const liveSearch = (q: string, allowEmpty = false) => {
    if (searchTimer.current !== null) clearTimeout(searchTimer.current);
    if ((q.trim().length < 2 && !(allowEmpty && q.trim() === "")) || q.startsWith("/")) {
      setHits([]);
      setFileHits([]);
      return;
    }
    const seq = ++searchSeq.current;
    searchTimer.current = setTimeout(() => {
      void (async () => {
        const b = await brain();
        if (seq !== searchSeq.current) return; // a newer query owns the strip
        const r = b.interpret(q, {
          gigs: props.gigs,
          nowMs: Date.now(),
          focusGig: props.focusGig,
          scheduleLabel: t(locale, "collections.schedule"),
          fileNameLabel: t(locale, "files.col_name"),
          fileCategoryLabel: t(locale, "files.col_category"),
        });
        // Only PURE searches paint live — commands wait for ↵ (cards stay
        // deliberate, never flicker while typing).
        if (seq !== searchSeq.current) return;
        if (r.kind === "jump") {
          setHits(r.hits);
          setFileHits(r.files);
          setSel(0);
        } else {
          setHits([]);
          setFileHits([]);
        }
      })();
    }, 140);
  };

  const openGigHit = (h: { gigId: string; label: string; split?: "day" | "people" }) => {
    searchSeq.current++; // cancel in-flight strips
    setQuery("");
    setHits([]);
    setFileHits([]);
    props.onJump(h.gigId, h.split ?? "day");
    props.tutorialTick("view");
    props.pushJournal(`jump → ${h.label}`, "muted");
  };
  const openFileHit = (f: { item_id: string }) => {
    searchSeq.current++;
    setQuery("");
    setHits([]);
    setFileHits([]);
    props.onOpenFile(f.item_id);
  };
  const openHit = (i: number) => {
    const gigCount = hits.length;
    if (i < gigCount) {
      openGigHit(hits[i]!);
      return true;
    }
    const f = fileHits[i - gigCount];
    if (f !== undefined) {
      openFileHit(f);
      return true;
    }
    return false;
  };

  const applyCards = async (toApply: readonly ApplyCard[]) => {
    if (editor === null || toApply.length === 0) return;
    let applied = 0;
    for (const c of toApply) {
      try {
        await editor.putFields(c.gigId, c.collection, c.itemId, c.patch);
        applied++;
      } catch {
        say(t(locale, "console.invalid_proposal"), "warn");
      }
    }
    setCards([]);
    setQuery("");
    if (applied > 0) {
      say(t(locale, "console.applied", { n: applied }), "ok", 5000);
      props.pushJournal(t(locale, "console.applied", { n: applied }), "ok");
      props.tutorialTick("apply");
    }
  };

  const applyPlan = async () => {
    if (editor === null || plan === null) return;
    say(t(locale, "console.plan_applying"), "work");
    const ids: string[] = [];
    let made = 0;
    try {
      const total = plan.gigs.length + plan.rows.length;
      for (const g of plan.gigs) {
        const { gigId } = await editor.createGig(g);
        ids.push(gigId);
        made++;
        say(t(locale, "console.plan_progress", { n: made, total, label: g["name"] ?? "" }), "work");
      }
      for (const r of plan.rows) {
        const gigId = /^new:\d+$/.test(r.gig_ref) ? ids[Number(r.gig_ref.slice(4))] : r.gig_ref;
        if (gigId === undefined) continue;
        await editor.createItem(gigId, r.collection, r.fields);
        made++;
        say(t(locale, "console.plan_progress", { n: made, total, label: tLabel(locale, `collections.${r.collection}`) }), "work");
      }
      setPlan(null);
      say(t(locale, "console.plan_done", { n: made }), "ok", 6000);
      props.pushJournal(t(locale, "console.plan_done", { n: made }), "ok");
    } catch {
      say(t(locale, "console.plan_failed", { n: made }), "warn");
    }
  };

  const undo = async () => {
    if (editor === null) return;
    const done = await editor.undoLast();
    say(t(locale, done ? "console.undone" : "console.nothing_to_undo"), done ? "ok" : "warn", 4000);
    if (done) {
      props.pushJournal(t(locale, "console.undone"), "accent");
      props.tutorialTick("undo");
    }
  };

  const seam = (key: MessageKey) => () => say(t(locale, key), "warn");

  const slashCmds = (): SlashCmd[] => {
    const fill = (q: string) => () => {
      setQuery(q);
      inputRef.current?.focus();
    };
    const go = (v: CockpitView) => () => {
      setQuery("");
      props.onView(v);
      props.tutorialTick("view");
    };
    return [
      { cmd: "/date", descKey: "console.slash.date", run: () => { setQuery(""); props.onNewGig(); } },
      { cmd: "/ligne", descKey: "console.slash.ligne", run: () => { setQuery(""); props.onAddRow(); } },
      { cmd: "/décale 30", descKey: "console.slash.decale", run: fill(locale === "fr" ? "décale tout de 30 min" : "push everything 30 min") },
      { cmd: "/set 23:00", descKey: "console.slash.set", run: fill(locale === "fr" ? "set à 23:00" : "set at 23:00") },
      { cmd: "/renomme", descKey: "console.slash.renomme", run: fill(locale === "fr" ? "renomme " : "rename ") },
      { cmd: "/range", descKey: "console.slash.range", run: fill(locale === "fr" ? "range " : "move ") },
      {
        cmd: "/audit",
        descKey: "console.slash.audit",
        run: () => {
          setQuery("");
          const gig = props.focusGig;
          if (gig !== null) {
            props.onDispatchMission?.("audit", gig.gig_id);
            say(t(locale, "console.mission_dispatched"), "ok", 4000);
          } else say(t(locale, "omnibar.ask.no_gig"), "warn");
        },
      },
      { cmd: "/avance", descKey: "console.slash.avance", run: fill(locale === "fr" ? "avance " : "advance ") },
      { cmd: "/pdf", descKey: "console.slash.pdf", run: () => { setQuery(""); window.print(); } },
      { cmd: "/mail", descKey: "console.slash.mail", run: seam("console.seam_p5") },
      { cmd: "/drive", descKey: "console.slash.drive", run: seam("console.seam_p4") },
      {
        cmd: "/ics",
        descKey: "console.slash.ics",
        run: () => {
          setQuery("");
          const gig = props.focusGig;
          if (gig !== null) {
            const a = document.createElement("a");
            a.href = `/api/gigs/${encodeURIComponent(gig.gig_id)}/ics`;
            a.download = "";
            a.click();
            say(t(locale, "console.ics_done"), "ok", 4000);
          } else say(t(locale, "omnibar.ask.no_gig"), "warn");
        },
      },
      { cmd: "/scan", descKey: "console.slash.scan", run: seam("console.seam_p4") },
      { cmd: "/save", descKey: "console.slash.save", run: () => { setQuery(""); props.onForceSave(); } },
      { cmd: "/undo", descKey: "console.slash.undo", run: () => { setQuery(""); void undo(); } },
      { cmd: "/gigs", descKey: "console.slash.gigs", run: go("gig") },
      { cmd: "/fichiers", descKey: "console.slash.fichiers", run: go("vault") },
      { cmd: "/rolodex", descKey: "console.slash.rolodex", run: go("rolo") },
      { cmd: "/réglages", descKey: "console.slash.reglages", run: go("settings") },
      { cmd: "/thème jour", descKey: "console.slash.theme", run: fill(locale === "fr" ? "thème jour" : "thème jour") },
      { cmd: "/langue en", descKey: "console.slash.langue", run: fill("langue en") },
      { cmd: "/aide", descKey: "console.slash.aide", run: () => { setQuery(""); props.onStartTutorial(); } },
      { cmd: "/coffre", descKey: "console.slash.fichiers", run: go("vault") },
      { cmd: "/nouvelle", descKey: "console.slash.date", run: () => { setQuery(""); props.onNewGig(); } },
      { cmd: "/thème nuit", descKey: "console.slash.theme", run: () => { setQuery(""); props.setPrefs({ theme: "night" }); } },
      { cmd: "/thème auto", descKey: "console.slash.theme", run: () => { setQuery(""); props.setPrefs({ theme: "auto" }); } },
      { cmd: "/en", descKey: "console.slash.langue", run: () => { setQuery(""); props.setPrefs({ lang: "en" }); } },
      { cmd: "/fr", descKey: "console.slash.langue", run: () => { setQuery(""); props.setPrefs({ lang: "fr" }); } },
    ];
  };

  const submit = async (overrideQuery?: string) => {
    const q = (overrideQuery ?? query).trim();
    // Slash palette: ↵ runs the first matching command.
    if (q.startsWith("/")) {
      const head = q.slice(1).toLowerCase().split(" ")[0] ?? "";
      const hit = slashCmds().find((c) => c.cmd.slice(1).toLowerCase().startsWith(head));
      if (hit !== undefined) hit.run();
      else say(t(locale, "console.slash_unknown"), "warn");
      return;
    }
    // Cards/plan pending + bare ↵ = tout appliquer.
    if (cards.length > 0 && q === "") {
      await applyCards(cards);
      return;
    }
    if (plan !== null && q === "") {
      await applyPlan();
      return;
    }
    if (q === "") return;
    // INGEST-ANYTHING: the console digests whatever lands in it.
    if (/^(sk-ant-|sk-|AIza|xai-|mst-?|gsk_)[A-Za-z0-9_-]{12,}$/.test(q)) {
      setQuery("");
      say(t(locale, "console.key_detected"), "warn");
      return;
    }
    if (/\.zip\b|tour ?designer.*(zip|export|backup)/i.test(q)) {
      setQuery("");
      say(t(locale, "console.zip_hint"), "ok");
      return;
    }
    pushLog("❯", "cmd", q);
    remember(q);
    const b = await brain();
    // MEGAPROMPT → straight to the server plan brain, BEFORE the multi-command
    // split or any local single-edit heuristic can hijack it (the airport bug:
    // a dictated "nouvelle date … set 1h-3h … hôtel … vol …" was caught by the
    // local set-time editor and never reached plan mode).
    if (b.looksLikeMegaprompt(q)) {
      await askServerBrain(q, "omnibar.ask.unknown");
      return;
    }
    // MULTI-COMMAND: «décale tout de 30 min ; set à 23h00 puis renomme…» —
    // one enter, one merged card batch (each card stays one real op).
    const segments = q.split(/\s*(?:;|\bpuis\b|\bthen\b)\s+/i).filter((x) => x.trim() !== "");
    if (segments.length > 1) {
      const merged: ApplyCard[] = [];
      let misses = 0;
      for (const seg of segments) {
        const rr = b.interpret(seg, {
          gigs: props.gigs,
          nowMs: Date.now(),
          focusGig: props.focusGig,
          scheduleLabel: t(locale, "collections.schedule"),
          fileNameLabel: t(locale, "files.col_name"),
          fileCategoryLabel: t(locale, "files.col_category"),
        });
        if (rr.kind === "propose") merged.push(rr.card);
        else if (rr.kind === "proposeBatch") merged.push(...rr.cards);
        else misses++;
      }
      if (merged.length > 0) {
        setCards(merged);
        setCardsContext(misses > 0 ? t(locale, "console.multi_partial", { n: misses }) : t(locale, "console.multi_ctx", { n: segments.length }));
        setQuery("");
        setAnswer(null);
        props.tutorialTick("cards");
        return;
      }
      // no local cards → the whole thing goes to the server brain as one ask
    }
    const ctx = {
      gigs: props.gigs,
      nowMs: Date.now(),
      focusGig: props.focusGig,
      scheduleLabel: t(locale, "collections.schedule"),
      fileNameLabel: t(locale, "files.col_name"),
      fileCategoryLabel: t(locale, "files.col_category"),
    };
    const r: OmnibarResult = b.interpret(q, ctx);
    switch (r.kind) {
      case "view":
        setQuery("");
        props.onView(r.view);
        props.tutorialTick("view");
        say(
          t(locale, "console.opened", {
            label: t(locale, r.view === "rolo" ? "nav.rolodex" : r.view === "vault" ? "nav.vault" : r.view === "settings" ? "nav.settings" : "nav.gig"),
          }),
          "ok",
          3000,
        );
        break;
      case "tour":
        setQuery("");
        props.onStartTutorial();
        break;
      case "pref":
        setQuery("");
        props.setPrefs(r);
        say(t(locale, "console.pref_applied", { value: r.theme ?? r.lang ?? r.units ?? "" }), "ok", 4000);
        props.pushJournal(t(locale, "console.pref_journal"), "ok");
        break;
      case "newdate":
        setQuery("");
        props.onNewGig({ city: r.city, day: r.dayOfMonth !== null ? String(r.dayOfMonth) : undefined });
        break;
      case "mission": {
        const gigId = r.gigId ?? props.focusGig?.gig_id ?? null;
        if (gigId === null) {
          say(t(locale, "omnibar.ask.no_gig"), "warn");
          break;
        }
        setQuery("");
        props.onDispatchMission?.(r.mission, gigId);
        say(t(locale, "console.mission_dispatched"), "ok", 4000);
        break;
      }
      case "propose":
        setCards([r.card]);
        setCardsContext(r.card.context);
        setQuery(""); // ↵ = tout appliquer, 1–9 = one (behavior contract §3)
        setAnswer(null);
        props.tutorialTick("cards");
        break;
      case "proposeBatch":
        setCards(r.cards);
        setCardsContext(r.context);
        setQuery("");
        setAnswer(null);
        props.tutorialTick("cards");
        break;
      case "upload":
        if (canWrite && props.uploadQueue !== undefined) {
          fileRef.current?.click();
          say(t(locale, "console.upload"), "ok", 6000);
        } else say(t(locale, "console.upload_unavailable"), "warn");
        break;
      case "jump": {
        const first = r.hits[0];
        const file = r.files[0];
        if (first !== undefined) {
          setQuery("");
          props.onJump(first.gigId, first.split ?? "day");
          say(t(locale, first.split === "people" ? "console.person_sheet" : "console.opened", { label: first.label, name: first.label }), "ok", 3000);
          props.pushJournal(`jump → ${first.label}`, "muted");
          props.tutorialTick("view");
        } else if (file !== undefined) {
          setQuery("");
          props.onOpenFile(file.item_id);
          say(t(locale, "console.file_found", { name: file.name }), "ok", 4000);
        } else {
          // No local hit at all — this is a question or a phrasing the
          // deterministic parser doesn't know: the server brain's turn.
          await askServerBrain(q, "console.not_understood");
        }
        break;
      }
      case "ask":
        // The local parser is out of its depth — hand the query to the REAL
        // server brain (keyless Vertex, apply-cards only). 503/offline keeps
        // the honest local message: the console never dead-ends.
        await askServerBrain(q, r.messageKey as MessageKey);
        break;
    }
  };

  /** POST /api/assist — the omnibarBrain contract, live. The server has
   * already validated every card against the registry; applying them still
   * mints client ops through the ONE Editor (mutation gate unchanged). */
  const askServerBrain = async (q: string, fallbackKey: MessageKey) => {
    say(t(locale, "console.thinking"), "work");
    try {
      const res = await fetch("/api/assist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ query: q, gig_id: props.focusGig?.gig_id, locale }),
      });
      if (res.status === 503) {
        say(t(locale, "console.offline_brain"), "warn");
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as {
        result?:
          | { kind: "answer"; text: string }
          | { kind: "jump"; gig_id: string }
          | { kind: "unknown" }
          | {
              kind: "cards";
              note: string | null;
              cards: { gig_id: string; collection: string; item_id: string; field: string; old_value: string; new_value: string }[];
            }
          | {
              kind: "plan";
              note: string | null;
              gigs: Record<string, string>[];
              rows: { gig_ref: string; collection: string; fields: Record<string, string> }[];
              inferred?: { field: string; value: string }[];
              personal_data?: boolean;
            };
      };
      const r = body.result;
      if (r === undefined || r.kind === "unknown") {
        say(t(locale, fallbackKey), "warn");
        return;
      }
      if (r.kind === "answer") {
        say(r.text, "ok");
        pushLog("◆", "ai", r.text);
        setQuery("");
        return;
      }
      if (r.kind === "jump") {
        setQuery("");
        props.onJump(r.gig_id, "day");
        return;
      }
      if (r.kind === "plan") {
        // MEGAPROMPT → a whole tour as ONE reviewable plan. Apply runs gigs
        // first (collecting fresh ids), then rows — every op real, undoable.
        setPlan({ gigs: r.gigs, rows: r.rows, note: r.note ?? "", inferred: r.inferred ?? [], personalData: r.personal_data ?? false });
        setQuery("");
        setAnswer(null);
        props.tutorialTick("cards");
        return;
      }
      const mapped: ApplyCard[] = r.cards.map((c) => ({
        context: tLabel(locale, `collections.${c.collection}`),
        gigId: c.gig_id,
        collection: c.collection,
        itemId: c.item_id,
        field: c.field,
        fieldLabel: tLabel(locale, `${c.collection}.${c.field}`),
        oldValue: c.old_value === "" ? "—" : c.old_value,
        newValue: c.new_value,
        patch: { [c.field]: c.new_value },
        sideEffects: [],
      }));
      setCards(mapped);
      setCardsContext(r.note ?? "");
      setQuery("");
      setAnswer(null);
      props.tutorialTick("cards");
    } catch {
      say(t(locale, "console.offline_brain"), "warn");
    }
  };

  const applyOne = (i: number) => {
    const c = cards[i];
    if (c !== undefined) void applyCards([c]);
  };

  const totalHits = hits.length + fileHits.length;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (totalHits > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSel((n) => Math.min(n + 1, totalHits - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSel((n) => Math.max(n - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (openHit(sel)) return;
      }
    }
    if (e.key === "Enter") {
      e.preventDefault();
      setHits([]);
      setFileHits([]);
      void submit();
      return;
    }
    if (e.key === "Escape") {
      setQuery("");
      setCards([]);
      setAnswer(null);
      setHits([]);
      setFileHits([]);
      return;
    }
    if (cards.length > 0 && query === "" && /^[1-9]$/.test(e.key)) {
      e.preventDefault();
      applyOne(Number(e.key) - 1);
    }
  };

  // Vocal-first: the mic TOGGLES a dictation session and the user WATCHES
  // themselves being heard — Web Speech streams the interim transcript into
  // the input live (feel-listened feedback), while MediaRecorder captures in
  // parallel for the accurate keyless-Vertex transcript that replaces it on
  // stop. NOTHING auto-sends: the transcript sits in the input, editable,
  // until the user hits ↵ (the apply-card gate stays the only mutation path).
  const voiceRef = useRef<{ stopSpeech?: () => void; rec: import("../omnibar/speech.ts").RecorderSession | null; timer: ReturnType<typeof setTimeout> | null } | null>(null);

  const stopMic = async () => {
    const v = voiceRef.current;
    voiceRef.current = null;
    setListening(false);
    if (v === null) return;
    if (v.timer !== null) clearTimeout(v.timer);
    v.stopSpeech?.();
    if (v.rec !== null) {
      say(t(locale, "console.transcribing"), "work");
      const text = await v.rec.stop(); // keyless Vertex — franglais-accurate
      if (text !== null && text.trim() !== "") {
        // Dictate → apply in ONE tap (airport law): the transcript submits
        // itself, the brain builds the plan/cards, and the user reviews + taps
        // apply once. A megaprompt lands everything without touching the keys.
        setQuery(text);
        setAnswer((a) => (a?.kind === "work" ? null : a));
        await submit(text);
        return;
      }
      if (query === "") say(t(locale, "console.voice_failed"), "warn", 4000);
    }
    setAnswer((a) => (a?.kind === "work" ? null : a));
    inputRef.current?.focus(); // correct it, then ↵
  };

  const startMic = async () => {
    if (listening) {
      await stopMic(); // tap again = done talking
      return;
    }
    setListening(true);
    say(t(locale, "console.listening"), "work");
    try {
      const sp = await import("../omnibar/speech.ts");
      // Live interim transcript — the "I can see it hearing me" channel.
      const speech = sp.startVoice(
        locale,
        (text) => {
          if (text !== "") setQuery(text);
        },
        () => undefined,
      );
      const rec = await sp.startRecorder();
      if (speech === null && rec === null) {
        setListening(false);
        say(t(locale, "console.voice_unsupported"), "warn", 4000);
        return;
      }
      // Safety stop: nobody dictates for 20s straight on the road.
      const timer = setTimeout(() => void stopMic(), 20_000);
      voiceRef.current = { stopSpeech: speech !== null ? () => speech.stop() : undefined, rec, timer };
    } catch {
      setListening(false);
      say(t(locale, "console.voice_failed"), "warn", 4000);
    }
  };

  const slashOpen = query.startsWith("/");
  const head = query.slice(1).toLowerCase().split(" ")[0] ?? "";
  const slashHits = slashOpen ? slashCmds().filter((c) => c.cmd.slice(1).toLowerCase().startsWith(head)) : [];

  return (
    <>
      {/* PLAN — megaprompt bulk creation, reviewable before ↵.
          Enterprise Intelligence Design Code: suggestion ≠ execution (nothing
          changes until ↵), self-explaining inference (what the server deduced
          from the words), and impact-before-action (records + personal-data
          flag) so the plan is never a black box. */}
      {plan !== null && (
        <div className="ck-cards">
          <div className="ck-cards-head">
            <span className="ck-cards-title">
              {t(locale, "console.plan_title", { g: plan.gigs.length, r: plan.rows.length })}
              {plan.note !== "" && <span className="ck-cards-ctx"> · {plan.note}</span>}
            </span>
            <span className="ck-hint">{t(locale, "console.propose_hints")}</span>
          </div>
          {plan.gigs.map((g, i) => (
            <div key={`g${i}`} className="ck-card">
              <span className="ck-card-n num">+</span>
              <span className="ck-card-field">{[g["date"], g["city"], g["name"]].filter(Boolean).join(" · ")}</span>
              <span className="ck-card-new num">GIG</span>
            </div>
          ))}
          {plan.rows.slice(0, 12).map((r, i) => (
            <div key={`r${i}`} className="ck-card">
              <span className="ck-card-n num">+</span>
              <span className="ck-card-field">
                {tLabel(locale, `collections.${r.collection}`)} · {Object.values(r.fields).slice(0, 3).join(" · ")}
              </span>
            </div>
          ))}
          {plan.rows.length > 12 && <div className="ck-hint">+{plan.rows.length - 12}…</div>}
          {plan.inferred.length > 0 && (
            <div className="ck-plan-ev">
              <span className="ck-plan-ev-k">◆ {t(locale, "console.plan_inferred")}</span>
              {plan.inferred.map((f, i) => (
                <span key={`inf${i}`} className="ck-plan-ev-v">
                  {tLabel(locale, INFERRED_LABEL[f.field] ?? `gigfiche.${f.field}`)} {f.value}
                </span>
              ))}
            </div>
          )}
          <div className="ck-plan-impact">{planImpact(locale, plan)}</div>
          <div className="if-actions" style={{ paddingLeft: 0 }}>
            <button type="button" className="ck-act ok" onClick={() => void applyPlan()}>{t(locale, "console.plan_apply")}</button>
            <button type="button" className="ck-act muted" onClick={() => setPlan(null)}>{t(locale, "console.plan_dismiss")}</button>
          </div>
        </div>
      )}

      {/* PROPOSE — apply cards (the only AI mutation gate) */}
      {cards.length > 0 && (
        <div className="ck-cards">
          <div className="ck-cards-head">
            <span className="ck-cards-title">
              {t(locale, "console.propose", { n: cards.length })}
              {cardsContext !== "" && <span className="ck-cards-ctx"> · {cardsContext}</span>}
            </span>
            <span className="ck-hint">{t(locale, "console.propose_hints")}</span>
          </div>
          {cards.map((c, i) => (
            <div key={`${c.itemId}:${c.field}`} className="ck-card">
              <span className="ck-card-n num">{i + 1}</span>
              <span className="ck-card-field">{c.context !== cardsContext ? `${c.context} · ` : ""}{c.fieldLabel}</span>
              <span className="ck-card-old num">{c.oldValue}</span>
              <span className="ck-card-arrow">→</span>
              <span className="ck-card-new num">{c.newValue}</span>
              <button type="button" className="ck-act ok" onClick={() => applyOne(i)}>
                {t(locale, "console.apply_one")}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* SLASH PALETTE — every command, filtered while typing */}
      {slashOpen && slashHits.length > 0 && (
        <div className="ck-slash">
          <div className="ck-cards-head">
            <span className="ck-cards-title">{t(locale, "console.slash_title")}</span>
            <span className="ck-hint">{t(locale, "console.slash_hint")}</span>
          </div>
          {slashHits.map((c) => (
            <button key={c.cmd} type="button" className="ck-slash-row" onClick={c.run}>
              <span className="ck-slash-cmd num">{c.cmd}</span>
              <span className="ck-slash-desc">{t(locale, c.descKey)}</span>
            </button>
          ))}
        </div>
      )}

      {/* TUTORIEL — interactive coach, advances on the real action */}
      {props.tutorial.step >= 0 && (
        <div className="ck-tour">
          <span className="ck-tour-pos">{t(locale, "tour.title", { pos: `${props.tutorial.step + 1} / ${props.tutorial.total}` })}</span>
          <span className="ck-tour-text">{t(locale, `tour.${props.tutorial.step + 1}` as MessageKey)}</span>
          <button type="button" className="ck-act ok" onClick={props.onTutorialNext}>{t(locale, "tour.next")}</button>
          <button type="button" className="ck-act muted" onClick={props.onTutorialQuit}>{t(locale, "tour.quit")}</button>
        </div>
      )}

      {/* RECENTS + quick commands — the empty console invites (DC law) */}
      {menuOpen && query === "" && canWrite && (
        <div className={`ck-quick${evaporating}`}>
          {recents.map((r) => (
            <button key={r} type="button" className="ck-chip" onMouseDown={(e) => { e.preventDefault(); setQuery(r); liveSearch(r); inputRef.current?.focus(); }}>
              ↻ {r.length > 34 ? `${r.slice(0, 32)}…` : r}
            </button>
          ))}
          <button type="button" className="ck-chip" onMouseDown={(e) => { e.preventDefault(); setQuery("/"); inputRef.current?.focus(); }}>
            / {t(locale, "console.quick_cmds")}
          </button>
          <button type="button" className="ck-chip" onMouseDown={(e) => { e.preventDefault(); props.onNewGig(); }}>
            + {t(locale, "newgig.title").toLowerCase()}
          </button>
        </div>
      )}

      {/* LIVE RESULTS — as you type: gigs (time-ranked) · people · files */}
      {menuOpen && totalHits > 0 && canWrite && (
        <div className={`ck-results${evaporating}`}>
          {hits.map((h, i) => (
            <button
              key={`${h.gigId}:${h.label}`}
              type="button"
              className={`ck-result${i === sel ? " active" : ""}`}
              onMouseEnter={() => setSel(i)}
              onClick={() => openGigHit(h)}
            >
              <span className="ck-result-tag">{h.split === "people" ? t(locale, "console.tag_person") : t(locale, "console.tag_gig")}</span>
              <span className="ck-result-label">{h.label}</span>
              <span className="ck-result-hint num">{h.hint}</span>
            </button>
          ))}
          {fileHits.map((f, i) => (
            <button
              key={f.item_id}
              type="button"
              className={`ck-result${hits.length + i === sel ? " active" : ""}`}
              onMouseEnter={() => setSel(hits.length + i)}
              onClick={() => openFileHit(f)}
            >
              <span className="ck-result-tag">{t(locale, "console.tag_file")}</span>
              <span className="ck-result-label">{f.name}</span>
              <span className="ck-result-hint num">{f.gig_city !== "" ? f.gig_city : f.gig_name}</span>
            </button>
          ))}
        </div>
      )}

      {/* CHAT LOG — vapor fade toward content, stays visible */}
      {log.length > 0 && (
        <div className="ck-log">
          {log.map((l, i) => (
            <div key={i} className="ck-log-line">
              <span className={`ck-log-mark ${l.tone}`}>{l.mark}</span>
              <span className="ck-log-text">{l.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* ANSWER LINE */}
      {answer !== null && (
        <div className={`ck-answer ${answer.kind}`}>
          <span>{answer.kind === "warn" ? "!" : answer.kind === "work" ? "◐" : "✓"}</span>
          <span>{answer.text}</span>
        </div>
      )}

      {/* COMMAND LINE */}
      {canWrite && (
        <div className={`ck-console${listening ? " listening" : ""}`}>
          {isMobile ? (
            <button
              type="button"
              className={`ck-mic${listening ? " listening" : ""}`}
              aria-label={t(locale, listening ? "omnibar.mic_stop" : "omnibar.mic_start")}
              aria-pressed={listening}
              onClick={() => void startMic()}
            >
              {listening ? "■" : "●"}
            </button>
          ) : (
            <span className="ck-prompt">❯</span>
          )}
          <input
            id="cockpit-console-input"
            ref={inputRef}
            value={query}
            placeholder={
              listening
                ? t(locale, "console.listening_placeholder")
                : props.view === "vault"
                  ? t(locale, "console.ph_vault")
                  : props.view === "rolo"
                    ? t(locale, "console.ph_rolo")
                    : props.view === "settings"
                      ? t(locale, "console.ph_settings")
                      : props.focusGig !== null
                        ? t(locale, isMobile ? "console.ph_gig_m" : "console.ph_gig")
                        : t(locale, isMobile ? "console.placeholder_mobile" : "console.placeholder_desktop")
            }
            onChange={(e) => {
              setQuery(e.target.value);
              liveSearch(e.target.value, e.target.value === "");
              if (answer !== null) setAnswer(null);
            }}
            onFocus={() => {
              setFocused(true);
              if (query === "") liveSearch("", true); // what's NEXT, instantly
            }}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onKeyDown={onKeyDown}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          {!isMobile && <span className="ck-hint">{t(locale, "console.hints")}</span>}
          {/* hidden picker for upload intents — the REAL offline-first queue */}
          <input
            ref={fileRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              const files = e.target.files;
              const gig = props.focusGig;
              if (files !== null && files.length > 0 && gig !== null && props.uploadQueue !== undefined) {
                for (const f of Array.from(files)) void props.uploadQueue.enqueue(gig.gig_id, f);
                say(t(locale, "console.upload_queued", { n: files.length }), "ok", 5000);
              }
              e.target.value = "";
            }}
          />
        </div>
      )}
    </>
  );
}
